import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import ResultCard from '../components/ResultCard';
import { Sparkles, ArrowLeft, Share2, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharedSearchPage = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sharedSearch, setSharedSearch] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedSearch();
  }, [shareId]);

  const fetchSharedSearch = async () => {
    try {
      const response = await axios.get(`${API}/search/shared/${shareId}`);
      setSharedSearch(response.data);
    } catch (error) {
      setError('Shared search not found or has expired');
    } finally {
      setLoading(false);
    }
  };

  const copyShareLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin text-primary">
          <Sparkles size={32} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-background to-black">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <Sparkles className="text-primary mx-auto mb-6" size={48} />
          <h1 className="text-2xl font-heading font-bold text-foreground mb-4">Search Not Found</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <Button onClick={() => navigate('/search')} data-testid="go-home-button">
            Go to Search
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-background to-black">
      <nav className="border-b border-border/50 backdrop-blur-md bg-card/30 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/search')}>
            <Sparkles className="text-primary" size={28} />
            <h1 className="text-2xl font-heading font-bold text-foreground">
              concurrenz<span className="text-primary">.AI</span>
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Eye size={16} />
              <span>{sharedSearch?.views || 0} views</span>
            </div>
            <Button variant="outline" size="sm" onClick={copyShareLink} data-testid="copy-link-button">
              <Share2 size={16} className="mr-2" />
              Copy Link
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-sm text-muted-foreground">Shared search result</p>
        </div>

        {sharedSearch && (
          <ResultCard 
            result={{
              query: sharedSearch.query,
              answer: sharedSearch.answer,
              image_url: sharedSearch.image_url,
              sources: []
            }} 
          />
        )}

        <div className="mt-8 text-center">
          <p className="text-muted-foreground mb-4">Want to ask your own questions?</p>
          <Button onClick={() => navigate('/search')} data-testid="try-search-button">
            <Sparkles size={16} className="mr-2" />
            Try concurrenz.AI
          </Button>
        </div>
      </main>
    </div>
  );
};

export default SharedSearchPage;
